import { useState, useEffect, useRef } from 'react';
import { MapPin } from 'lucide-react';

// Comprehensive list of destinations for autocomplete
const DESTINATIONS = [
  // Indian Cities
  { name: "Delhi", code: "DEL", country: "India" },
  { name: "Mumbai", code: "BOM", country: "India" },
  { name: "Bangalore", code: "BLR", country: "India" },
  { name: "Chennai", code: "MAA", country: "India" },
  { name: "Kolkata", code: "CCU", country: "India" },
  { name: "Hyderabad", code: "HYD", country: "India" },
  { name: "Ahmedabad", code: "AMD", country: "India" },
  { name: "Pune", code: "PNQ", country: "India" },
  { name: "Jaipur", code: "JAI", country: "India" },
  { name: "Lucknow", code: "LKO", country: "India" },
  { name: "Goa", code: "GOI", country: "India" },
  { name: "Kochi", code: "COK", country: "India" },
  { name: "Thiruvananthapuram", code: "TRV", country: "India" },
  { name: "Srinagar", code: "SXR", country: "India" },
  { name: "Varanasi", code: "VNS", country: "India" },
  { name: "Amritsar", code: "ATQ", country: "India" },
  { name: "Udaipur", code: "UDR", country: "India" },
  { name: "Jodhpur", code: "JDH", country: "India" },
  { name: "Shimla", code: "SLV", country: "India" },
  { name: "Manali", code: "MNL", country: "India" },
  { name: "Chandigarh", code: "IXC", country: "India" },
  { name: "Indore", code: "IDR", country: "India" },
  { name: "Bhopal", code: "BHO", country: "India" },
  { name: "Nagpur", code: "NAG", country: "India" },
  { name: "Patna", code: "PAT", country: "India" },
  { name: "Ranchi", code: "IXR", country: "India" },
  { name: "Guwahati", code: "GAU", country: "India" },
  { name: "Visakhapatnam", code: "VTZ", country: "India" },
  { name: "Coimbatore", code: "CJB", country: "India" },
  { name: "Madurai", code: "IXM", country: "India" },
  { name: "Mysore", code: "MYQ", country: "India" },
  { name: "Agra", code: "AGR", country: "India" },
  { name: "Darjeeling", code: "DAR", country: "India" },
  { name: "Rishikesh", code: "RSK", country: "India" },
  { name: "Haridwar", code: "HRD", country: "India" },
  { name: "Leh", code: "IXL", country: "India" },
  { name: "Andaman", code: "IXZ", country: "India" },
  
  // International Cities
  { name: "Dubai", code: "DXB", country: "UAE" },
  { name: "Abu Dhabi", code: "AUH", country: "UAE" },
  { name: "Singapore", code: "SIN", country: "Singapore" },
  { name: "Bangkok", code: "BKK", country: "Thailand" },
  { name: "Phuket", code: "HKT", country: "Thailand" },
  { name: "Pattaya", code: "PTY", country: "Thailand" },
  { name: "Kuala Lumpur", code: "KUL", country: "Malaysia" },
  { name: "Bali", code: "DPS", country: "Indonesia" },
  { name: "Jakarta", code: "CGK", country: "Indonesia" },
  { name: "Male", code: "MLE", country: "Maldives" },
  { name: "Colombo", code: "CMB", country: "Sri Lanka" },
  { name: "Kathmandu", code: "KTM", country: "Nepal" },
  { name: "Hong Kong", code: "HKG", country: "China" },
  { name: "Macau", code: "MFM", country: "China" },
  { name: "Tokyo", code: "NRT", country: "Japan" },
  { name: "Seoul", code: "ICN", country: "South Korea" },
  { name: "Sydney", code: "SYD", country: "Australia" },
  { name: "Melbourne", code: "MEL", country: "Australia" },
  { name: "London", code: "LHR", country: "UK" },
  { name: "Paris", code: "CDG", country: "France" },
  { name: "Amsterdam", code: "AMS", country: "Netherlands" },
  { name: "Frankfurt", code: "FRA", country: "Germany" },
  { name: "Munich", code: "MUC", country: "Germany" },
  { name: "Zurich", code: "ZRH", country: "Switzerland" },
  { name: "Geneva", code: "GVA", country: "Switzerland" },
  { name: "Rome", code: "FCO", country: "Italy" },
  { name: "Venice", code: "VCE", country: "Italy" },
  { name: "Milan", code: "MXP", country: "Italy" },
  { name: "Barcelona", code: "BCN", country: "Spain" },
  { name: "Madrid", code: "MAD", country: "Spain" },
  { name: "Athens", code: "ATH", country: "Greece" },
  { name: "Santorini", code: "JTR", country: "Greece" },
  { name: "Istanbul", code: "IST", country: "Turkey" },
  { name: "Cairo", code: "CAI", country: "Egypt" },
  { name: "Mauritius", code: "MRU", country: "Mauritius" },
  { name: "Seychelles", code: "SEZ", country: "Seychelles" },
  { name: "New York", code: "JFK", country: "USA" },
  { name: "Los Angeles", code: "LAX", country: "USA" },
  { name: "San Francisco", code: "SFO", country: "USA" },
  { name: "Las Vegas", code: "LAS", country: "USA" },
  { name: "Miami", code: "MIA", country: "USA" },
  { name: "Toronto", code: "YYZ", country: "Canada" },
  { name: "Vancouver", code: "YVR", country: "Canada" },
  { name: "Dublin", code: "DUB", country: "Ireland" },
];

export default function DestinationAutocomplete({ 
  value, 
  onChange, 
  placeholder = "Enter city",
  label,
  icon: Icon = MapPin,
  className = ""
}) {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    setQuery(inputValue);
    onChange(inputValue);
    setHighlightedIndex(-1);

    if (inputValue.length > 0) {
      const filtered = DESTINATIONS.filter(dest => 
        dest.name.toLowerCase().includes(inputValue.toLowerCase()) ||
        dest.code.toLowerCase().includes(inputValue.toLowerCase()) ||
        dest.country.toLowerCase().includes(inputValue.toLowerCase())
      ).slice(0, 8);
      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSelect = (dest) => {
    setQuery(dest.name);
    onChange(dest.name);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => 
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault();
      handleSelect(suggestions[highlightedIndex]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      {label && (
        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">
          {label}
        </label>
      )}
      <div className="relative">
        <Icon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0) setShowSuggestions(true);
          }}
          placeholder={placeholder}
          className="w-full pl-12 pr-4 py-3 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
          data-testid="destination-autocomplete"
          autoComplete="off"
        />
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-xl shadow-lg border border-slate-200 max-h-64 overflow-y-auto" data-testid="suggestions-dropdown">
          {suggestions.map((dest, index) => (
            <button
              key={`${dest.code}-${index}`}
              type="button"
              onClick={() => handleSelect(dest)}
              className={`w-full px-4 py-3 flex items-center gap-3 text-left transition-colors ${
                index === highlightedIndex 
                  ? 'bg-teal-50 text-teal-700' 
                  : 'hover:bg-slate-50'
              }`}
              data-testid={`suggestion-${dest.code}`}
            >
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-slate-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-900 truncate">{dest.name}</p>
                <p className="text-sm text-slate-500">{dest.country} • {dest.code}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export { DESTINATIONS };
